import _ from 'lodash';
import i18n from 'i18next';

import { LocationDescriptor } from 'history';

import * as api from '../../api';
import * as FormSetUtils from '../formSet';
import * as logger from '../../logger';
import * as store from '../../reducers/store';

import type { Form, Forms, FormSet, FormSets } from '../../reducers/store';
import { getPreferredPhoneOptions } from '../../components/utils';
import { validateFormFieldValue } from 'common/dist/utils/previsitFormField';
import { RouteComponentProps } from 'react-router-dom';
import { Status } from 'vivaa-api/dist/models/previsit/form';
import { checkConditions } from './condition';
import { fieldsScores } from './condition/score';
import { parseValue } from 'common/dist/utils/types';
import { hasRecommendationForm, isRecommendationForm } from './recommondation';
import { SectionType } from '../review';

///////////////////////////////////////////////////
//////TYPES

export enum FormTypes {
  Main = 'MAIN',
  Recommendation = 'RECOMMENDATION',
  All = 'ALL',
}

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

const primitiveHardConditions = [Primitive.PatientPreferredPhone];

interface ConditionalFieldValues {
  hasPain: boolean;
  hasPainImproved: boolean;
  hasPainWorsened: boolean;
  hasPainDuringDayToDayActivities: boolean;
  hasTakenMedication: boolean;
  hasSideEffect: boolean;
  hasNumbnessOrTingling: boolean;
  hasUsedBrace: boolean;
  hasCheckedBloodSugarReadings: boolean;
  hasMonitoredBloodPressure: boolean;
  hasFluShot: boolean;
  hasPneumoniaShot: boolean;
  hasExercised: boolean;
  hasDrankAlcohol: boolean;
  hasHeelPainDurationDays: boolean;
  hasHeelPainDurationMonths: boolean;
  hasHeelPainDurationYears: boolean;
  hasHeelPainPreviousProvider: boolean;
  hasNailPainDurationDays: boolean;
  hasNailPainDurationMonths: boolean;
  hasNailPainDurationYears: boolean;
  hasNailPainPreviousProvider: boolean;
  hasNailPainDiabetic: boolean;
  hasNailPainNumbness: boolean;
  hasNailPainPreviousXray: boolean;

  // Generic Foot
  hasGenericFootOtherReason: boolean;
  hasGenericFootDays: boolean;
  hasGenericFootMonths: boolean;
  hasGenericFootYears: boolean;
  hasGenericFootContext: boolean;
  hasGenericFootPainScale: boolean;

  // Park hip pain
  hasHipPainDays: boolean;
  hasHipPainMonths: boolean;
  hasHipPainYears: boolean;
  hasHipPainLimp: boolean;
  hasHipPainNight: boolean;
  hasHipPainActivities: boolean;
  hasHipPainStairs: boolean;
  hasHipPainWork: boolean;
  hasHipPainPreviousInjury: boolean;

  // Park knee pain
  hasKneePainRight: boolean;
  hasKneePainLeft: boolean;
  hasKneePainDays: boolean;
  hasKneePainMonths: boolean;
  hasKneePainYears: boolean;
  hasKneePainLimp: boolean;
  hasKneePainNight: boolean;
  hasKneePainActivities: boolean;
  hasKneePainStairs: boolean;
  hasKneePainWork: boolean;
  hasKneePainPreviousInjury: boolean;

  // Ahuero NP Ankle Pain
  hasAnklePainDays: boolean;
  hasAnklePainMonths: boolean;
  hasAnklePainYears: boolean;
  hasAnklePainPreviousLocation: boolean;

  // Ahuero NP Ankle Pain
  hasFootPainToesHurt: boolean;
  hasFootPainDays: boolean;
  hasFootPainMonths: boolean;
  hasFootPainYears: boolean;
  hasFootPainPreviousLocation: boolean;

  // HM Diabetic foot care
  hasFootCareDiabetic: boolean; // 2.1 == Yes
  hasFootCareDiabeticAdult: boolean; // 2.2 == Type 2 (Adult)
  hasFootCareCheckAtHome: boolean; // 2.5 == Yes
  hasFootCareSkinChanges: boolean; // 3.0 === Yes
  hasFootCareNumbness: boolean; // 3.2 == Yes
  hasFootCareUlceration: boolean; // 3.4 == Yes
  hasFootCareSurgery: boolean; // 4.1 == Yes
  hasFootCareAmputation: boolean; // 4.2 == Yes
  hasFootCareFootwear: boolean; // 5.1 == Yes
  hasFootCareFootwearOtherFacility: boolean; // 5.3 == Other
  hasFootCareUseTobacco: boolean; // 5.6 == Yes

  // Desai post op general
  hasFootPain: boolean;
  hasNerveBlock: boolean;
  hasAdditionalSymptoms: boolean;
  hasAdditionalComments: boolean;

  // Ahuero NP Plantar Fasciitis
  hasPlantarFasciitisDays: boolean;
  hasPlantarFasciitisMonths: boolean;
  hasPlantarFasciitisYears: boolean;
  hasPlantarFasciitisWalkingAidKind: boolean;
  hasPlantarFasciitisPreviousLocation: boolean;

  // Ahuero NP Achilles Tendonitis
  hasAchillesTendonitisDays: boolean;
  hasAchillesTendonitisMonths: boolean;
  hasAchillesTendonitisYears: boolean;
  hasAchillesTendonitisPreviousLocation: boolean;

  // Labbe Shoulder
  hasShoulderDays: boolean;
  hasShoulderMonths: boolean;
  hasShoulderYears: boolean;
  hasShoulderExercises: boolean;
  hasShoulderThrow: boolean;
  hasShoulderJob: boolean;
  hasShoulderDetails: boolean;

  // Labbe Knee
  hasKneeDays: boolean;
  hasKneeMonths: boolean;
  hasKneeYears: boolean;
  hasKneeJob: boolean;
  hasKneeDetails: boolean;

  // Standard Foot
  hasFootDays: boolean;
  hasFootMonths: boolean;
  hasFootYears: boolean;
  hasFootJob: boolean;
  hasFootDetails: boolean;

  // Palmer Lower Extremity
  hasLowextBackPainLocation: boolean;
  hasLowextBackPainSide: boolean;
  hasLowextBackPainSeverity: boolean;
  hasLowextBackPainDuration: boolean;
  hasLowextBackPainDays: boolean;
  hasLowextBackPainMonths: boolean;
  hasLowextBackPainYears: boolean;
  hasLowextLegPainLocation: boolean;
  hasLowextLegPainSide: boolean;
  hasLowextLegPainSeverity: boolean;
  hasLowextLegPainDuration: boolean;
  hasLowextLegPainDays: boolean;
  hasLowextLegPainMonths: boolean;
  hasLowextLegPainYears: boolean;
  hasLowextLegWeaknessSide: boolean;
  hasLowextLegWeaknessLocation: boolean;
  hasLowextSensationSide: boolean;
  hasLowextSensationLocation: boolean;
  hasLowextWorkOff: boolean;
  hasLowextSurgeryInfo: boolean;
  hasLowextInjectionsDate: boolean;

  // Ahuero fu general
  hasOtherComplaint: boolean;
}

///////////////////////////////////////////////////
//////PUBLIC

export function getSourceFieldValue(
  value: string,
  primitive: api.PrevisitFormTemplateFieldConfig.Primitive,
): any[] {
  try {
    switch (primitive) {
      case Primitive.MultiString:
      case Primitive.MultiEnum:
      case Primitive.PatientNCPDPPharmacies:
        return JSON.parse(value);
      case Primitive.PatientInsurance:
      case Primitive.PatientInsuranceCardPhotoUpload:
      case Primitive.PatientInsuranceCardUpload:
        return [JSON.parse(value)];
      case Primitive.Registration:
        const parsedValue = parseValue<{
          reconciliationStatus?: api.Register.ReconciliationStatus;
        }>(value);
        return parsedValue?.reconciliationStatus
          ? [parsedValue?.reconciliationStatus]
          : [value];
      case Primitive.PatientCareProvidersPicker:
        const parsed = parseValue<{
          pcpNotFound?: boolean;
          selectedPcp?: api.PrevisitPatientCareTeam.PatientPCP;
        }>(value);
        return parsed?.pcpNotFound
          ? [api.PrevisitPatientCareTeam.PcpSelectionStatus.NoPcpFound]
          : [parsed?.selectedPcp];
      default:
        return [value];
    }
  } catch (err) {
    return [value];
  }
}

export function isValueContained(array: string[], value: string[]): boolean {
  return array.some((needle) => value.includes(needle));
}

export const getMaybeDocumentSignerFormField = (
  forms: store.Forms,
): api.PrevisitFormField.PrevisitFormFieldWithConfig | undefined => {
  for (const form of Object.values(forms)) {
    for (const field of form.fields) {
      if (field.primitive === Primitive.DocumentSigner) {
        return field;
      }
    }
  }
};

export const shouldDocumentBeShown = (
  conditions: api.PrevisitDocument.Condition[],
  fields: store.FormFieldWithConfig[],
): boolean => {
  if (!conditions.length) return true;
  const isRejected = conditions
    .filter((cond) => cond.rejectValues)
    .some((condition) => {
      const existingFormField = fields.find(
        (field) =>
          field.previsitFormTemplateFieldConfigId ===
          condition.sourcePrevisitFormTemplateFieldConfigId,
      );
      if (!existingFormField || !condition.rejectValues) return false;
      const value = getSourceFieldValue(
        existingFormField.value,
        existingFormField.primitive,
      );
      return condition.rejectValues.some((rejectValue) => {
        const rejectedValue = getSourceFieldValue(
          rejectValue,
          existingFormField.primitive,
        );
        return (
          isValueContained(rejectedValue, value) ||
          _.some(value, rejectedValue[0])
        );
      });
    });
  if (isRejected) return false;
  const acceptValuesConditions = conditions.filter((cond) => cond.acceptValues);
  if (!acceptValuesConditions.length) return true;
  const isAccepted = acceptValuesConditions.some((condition) => {
    const existingFormField = fields.find(
      (field) =>
        field.previsitFormTemplateFieldConfigId ===
        condition.sourcePrevisitFormTemplateFieldConfigId,
    );
    if (!existingFormField || !condition.acceptValues) return false;
    const value = getSourceFieldValue(
      existingFormField.value,
      existingFormField.primitive,
    );
    return condition.acceptValues.some((acceptValue) => {
      const acceptedValue = getSourceFieldValue(
        acceptValue,
        existingFormField.primitive,
      );

      return (
        isValueContained(acceptedValue, value) ||
        _.some(value, acceptedValue[0])
      );
    });
  });
  return isAccepted;
};

export function getVisibleFields({
  fields,
  sourceFields,
  patient,
}: {
  fields: store.FormFieldWithConfig[];
  sourceFields: store.FormFieldWithConfig[];
  patient: api.Patient.Patient | null;
}) {
  return fields.filter((field) => !checkIsHidden(field, sourceFields, patient));
}

export function checkIsHidden(
  field: store.FormFieldWithConfig,
  fields: store.FormFieldWithConfig[],
  patient: api.Patient.Patient | null,
): boolean {
  const { name, conditions, page, rank } = field;
  if (field.source === api.PrevisitFormField.Source.Synced) return true;
  if (conditions.length) {
    return !checkConditions({
      conditions,
      page,
      rank,
      fields,
      patient,
    });
  }

  if (primitiveHardConditions.includes(field.primitive)) {
    return !checkHardCondition(field, fields, patient);
  }

  const {
    hasPain,
    hasPainImproved,
    hasPainWorsened,
    hasPainDuringDayToDayActivities,
    hasTakenMedication,
    hasSideEffect,
    hasNumbnessOrTingling,
    hasCheckedBloodSugarReadings,
    hasMonitoredBloodPressure,
    hasFluShot,
    hasPneumoniaShot,
    hasExercised,
    hasDrankAlcohol,
    hasHeelPainDurationDays,
    hasHeelPainDurationMonths,
    hasHeelPainDurationYears,
    hasHeelPainPreviousProvider,
    hasNailPainDurationDays,
    hasNailPainDurationMonths,
    hasNailPainDurationYears,
    hasNailPainPreviousProvider,
    hasNailPainDiabetic,
    hasNailPainNumbness,
    hasNailPainPreviousXray,

    // desai post op general
    hasFootPain,
    hasNerveBlock,
    hasAdditionalSymptoms,
    hasAdditionalComments,

    // Generic Foot
    hasGenericFootOtherReason,
    hasGenericFootDays,
    hasGenericFootMonths,
    hasGenericFootYears,
    hasGenericFootContext,
    hasGenericFootPainScale,

    // Park hip pain
    hasHipPainDays,
    hasHipPainMonths,
    hasHipPainYears,
    hasHipPainLimp,
    hasHipPainNight,
    hasHipPainActivities,
    hasHipPainStairs,
    hasHipPainWork,
    hasHipPainPreviousInjury,

    // Park knee pain
    hasKneePainRight,
    hasKneePainLeft,
    hasKneePainDays,
    hasKneePainMonths,
    hasKneePainYears,
    hasKneePainLimp,
    hasKneePainNight,
    hasKneePainActivities,
    hasKneePainStairs,
    hasKneePainWork,
    hasKneePainPreviousInjury,

    // Ahuero NP Ankle Pain
    hasAnklePainDays,
    hasAnklePainMonths,
    hasAnklePainYears,
    hasAnklePainPreviousLocation,

    // Ahuero NP Foot Pain
    hasFootPainToesHurt,
    hasFootPainDays,
    hasFootPainMonths,
    hasFootPainYears,
    hasFootPainPreviousLocation,

    // HM Diabetic foot care
    hasFootCareDiabetic,
    hasFootCareDiabeticAdult,
    hasFootCareCheckAtHome,
    hasFootCareSkinChanges,
    hasFootCareNumbness,
    hasFootCareUlceration,
    hasFootCareSurgery,
    hasFootCareAmputation,
    hasFootCareFootwear,
    hasFootCareFootwearOtherFacility,
    hasFootCareUseTobacco,

    // Ahuero NP Plantar Fasciitis
    hasPlantarFasciitisDays,
    hasPlantarFasciitisMonths,
    hasPlantarFasciitisYears,
    hasPlantarFasciitisWalkingAidKind,
    hasPlantarFasciitisPreviousLocation,

    // Ahuero NP Achilles Tendonitis Pain
    hasAchillesTendonitisDays,
    hasAchillesTendonitisMonths,
    hasAchillesTendonitisYears,
    hasAchillesTendonitisPreviousLocation,

    // Labbe Shoulder
    hasShoulderDays,
    hasShoulderMonths,
    hasShoulderYears,
    hasShoulderExercises,
    hasShoulderThrow,
    hasShoulderJob,
    hasShoulderDetails,

    // Labbe Knee
    hasKneeDays,
    hasKneeMonths,
    hasKneeYears,
    hasKneeJob,
    hasKneeDetails,

    // Standard Foot
    hasFootDays,
    hasFootMonths,
    hasFootYears,
    hasFootJob,
    hasFootDetails,

    // Palmer Lower Extremity
    hasLowextBackPainLocation,
    hasLowextBackPainSide,
    hasLowextBackPainSeverity,
    hasLowextBackPainDuration,
    hasLowextBackPainDays,
    hasLowextBackPainMonths,
    hasLowextBackPainYears,
    hasLowextLegPainLocation,
    hasLowextLegPainSide,
    hasLowextLegPainSeverity,
    hasLowextLegPainDuration,
    hasLowextLegPainDays,
    hasLowextLegPainMonths,
    hasLowextLegPainYears,
    hasLowextLegWeaknessSide,
    hasLowextLegWeaknessLocation,
    hasLowextSensationSide,
    hasLowextSensationLocation,
    hasLowextWorkOff,
    hasLowextSurgeryInfo,
    hasLowextInjectionsDate,

    // Ahuero fu general
    hasOtherComplaint,
  } = getConditionalFieldValues(fields);

  // gleason post op rechecks + ficke standard recheck + fogle
  if (name === 'pain_time' && !hasPain) {
    return true;
  }
  // fogle
  if (name === 'pain_improved_percentage' && !hasPainImproved) {
    return true;
  }
  if (name === 'pain_worsened_percentage' && !hasPainWorsened) {
    return true;
  }
  if (
    name === 'activities_cause_pain_options' &&
    !hasPainDuringDayToDayActivities
  ) {
    return true;
  }
  if (name === 'medication_refill_requests' && !hasTakenMedication) {
    return true;
  }

  // ficke standard rechecks
  if (name === 'activities_causing_side_effects_freeform') {
    return true;
  }
  if (name === 'side_effects_frequency_options' && !hasNumbnessOrTingling) {
    return true;
  }

  // ficke post-ops
  // w/ carpal tunnel
  if (name === 'bracing_mitigation_boolean' && !hasSideEffect) {
    return true;
  }
  // w/ nerve repair surgery
  if (name === 'sensation_change_options' && !hasSideEffect) {
    return true;
  }
  // w/ trigger finger release surgery
  if (name === 'experience_clicking_or_locking_boolean' && !hasSideEffect) {
    return true;
  }

  // naik diabetes
  if (name === 'blood_sugar_levels_options' && !hasCheckedBloodSugarReadings) {
    return true;
  } else if (
    name === 'range_of_fasting_readings' &&
    !hasCheckedBloodSugarReadings
  ) {
    return true;
  } else if (
    name === 'range_of_readings_after_meals' &&
    !hasCheckedBloodSugarReadings
  ) {
    return true;
  }
  // naik hypertension
  if (
    name === 'typical_systolic_blood_pressure_readings_options' &&
    !hasMonitoredBloodPressure
  ) {
    return true;
  }
  if (
    name === 'typical_diastolic_blood_pressure_readings_options' &&
    !hasMonitoredBloodPressure
  ) {
    return true;
  }

  // dignity annual wellness
  if (name === 'when_flu_shot' && !hasFluShot) {
    return true;
  } else if (name === 'when_pneumonia_shot' && !hasPneumoniaShot) {
    return true;
  } else if (name === 'avg_minutes_exercised' && !hasExercised) {
    return true;
  } else if (name === 'one_week_alcohol_count' && !hasDrankAlcohol) {
    return true;
  }

  // desai heel pain
  if (name === 'heel_pain_days_duration' && !hasHeelPainDurationDays) {
    return true;
  } else if (
    name === 'heel_pain_months_duration' &&
    !hasHeelPainDurationMonths
  ) {
    return true;
  } else if (name === 'heel_pain_years_duration' && !hasHeelPainDurationYears) {
    return true;
  } else if (
    name === 'heel_pain_previous_location' &&
    !hasHeelPainPreviousProvider
  ) {
    return true;
  }

  // desai nail pain
  if (name === 'nail_pain_days_duration' && !hasNailPainDurationDays) {
    return true;
  } else if (
    name === 'nail_pain_months_duration' &&
    !hasNailPainDurationMonths
  ) {
    return true;
  } else if (name === 'nail_pain_years_duration' && !hasNailPainDurationYears) {
    return true;
  } else if (
    name === 'nail_pain_previous_location' &&
    !hasNailPainPreviousProvider
  ) {
    return true;
  } else if (name === 'nail_pain_diabetic_type' && !hasNailPainDiabetic) {
    return true;
  } else if (name === 'nail_pain_numbness_location' && !hasNailPainNumbness) {
    return true;
  } else if (
    name === 'nail_pain_previous_xray_location' &&
    !hasNailPainPreviousXray
  ) {
    return true;
  }

  // desai post op general
  switch (name) {
    case 'pain_description':
      return !hasFootPain;
    case 'pain_duration':
      return !hasFootPain;
    case 'pain_self_treatment':
      return !hasFootPain;
    case 'nerve_block_duration':
      return !hasNerveBlock;
    case 'other_symptoms':
      return !hasAdditionalSymptoms;
    case 'something_to_share_details':
      return !hasAdditionalComments;
  }

  // Generic Foot
  switch (name) {
    case 'hm_new_ortho_patient_cc_other':
      return !hasGenericFootOtherReason;
    case 'hm_new_ortho_days_duration':
      return !hasGenericFootDays;
    case 'hm_new_ortho_months_duration':
      return !hasGenericFootMonths;
    case 'hm_new_ortho_years_duration':
      return !hasGenericFootYears;
    case 'hm_new_ortho_patient_context':
      return !hasGenericFootContext;
    case 'hm_new_ortho_patient_pain_scale':
      return !hasGenericFootPainScale;
  }

  // Park hip pain
  switch (name) {
    case 'hip_pain_days_duration':
      return !hasHipPainDays;
    case 'hip_pain_months_duration':
      return !hasHipPainMonths;
    case 'hip_pain_years_duration':
      return !hasHipPainYears;
    case 'hip_pain_limp_severity':
      return !hasHipPainLimp;
    case 'hip_pain_night_pain_severity':
      return !hasHipPainNight;
    case 'hip_pain_daily_activities_severity':
      return !hasHipPainActivities;
    case 'hip_pain_stairs_severity':
      return !hasHipPainStairs;
    case 'hip_pain_work_severity':
      return !hasHipPainWork;
    case 'hip_pain_previous_injury_time':
      return !hasHipPainPreviousInjury;
  }

  // Park knee pain
  switch (name) {
    case 'knee_pain_right_location':
      return !hasKneePainRight;
    case 'knee_pain_left_location':
      return !hasKneePainLeft;
    case 'knee_pain_days_duration':
      return !hasKneePainDays;
    case 'knee_pain_months_duration':
      return !hasKneePainMonths;
    case 'knee_pain_years_duration':
      return !hasKneePainYears;
    case 'knee_pain_limp_severity':
      return !hasKneePainLimp;
    case 'knee_pain_night_pain_severity':
      return !hasKneePainNight;
    case 'knee_pain_daily_activities_severity':
      return !hasKneePainActivities;
    case 'knee_pain_stairs_severity':
      return !hasKneePainStairs;
    case 'knee_pain_work_severity':
      return !hasKneePainWork;
    case 'knee_pain_previous_injury_time':
      return !hasKneePainPreviousInjury;
  }

  // Ahuero ankle pain
  switch (name) {
    case 'ankle_pain_days_duration':
      return !hasAnklePainDays;
    case 'ankle_pain_months_duration':
      return !hasAnklePainMonths;
    case 'ankle_pain_years_duration':
      return !hasAnklePainYears;
    case 'ankle_pain_previous_location':
      return !hasAnklePainPreviousLocation;
  }

  // Ahuero foot pain
  switch (name) {
    case 'foot_pain_toes_hurt':
      return !hasFootPainToesHurt;
    case 'foot_pain_days_duration':
      return !hasFootPainDays;
    case 'foot_pain_months_duration':
      return !hasFootPainMonths;
    case 'foot_pain_years_duration':
      return !hasFootPainYears;
    case 'foot_pain_previous_location':
      return !hasFootPainPreviousLocation;
  }

  // HM Diabetic foot care
  switch (name) {
    case 'hm_diabetic_foot_care_diabetes_type':
      return !hasFootCareDiabetic;
    case 'hm_diabetic_foot_care_diabetes_how_long':
      return !hasFootCareDiabeticAdult;
    case 'hm_diabetic_foot_care_diabetes_management':
      return !hasFootCareDiabetic;
    case 'hm_diabetic_foot_care_diabetes_check_sugar':
      return !hasFootCareDiabetic;
    case 'hm_diabetic_foot_care_diabetes_check_sugar_machine':
      return !hasFootCareCheckAtHome;
    case 'hm_diabetic_foot_care_diabetes_check_sugar_last_reading':
      return !hasFootCareCheckAtHome;
    case 'hm_diabetic_foot_care_diabetes_check_sugar_hemoglobin':
      return !hasFootCareDiabetic;
    case 'hm_diabetic_foot_care_skin_changes_location':
      return !hasFootCareSkinChanges;
    case 'hm_diabetic_foot_care_numbness_location':
      return !hasFootCareNumbness;
    case 'hm_diabetic_foot_care_ulceration_active':
      return !hasFootCareUlceration;
    case 'hm_diabetic_foot_care_amputation':
      return !hasFootCareSurgery;
    case 'hm_diabetic_foot_care_amputation_where':
      return !hasFootCareAmputation;
    case 'hm_diabetic_foot_care_footwear_when':
      return !hasFootCareFootwear;
    case 'hm_diabetic_foot_care_footwear_where':
      return !hasFootCareFootwear;
    case 'hm_diabetic_foot_care_footwear_where_name':
      return !hasFootCareFootwearOtherFacility;
    case 'hm_diabetic_foot_care_tobacco_how_long':
      return !hasFootCareUseTobacco;
  }

  // Ahuero Plantar Fasciitis
  switch (name) {
    case 'plantar_fasciitis_days_duration':
      return !hasPlantarFasciitisDays;
    case 'plantar_fasciitis_months_duration':
      return !hasPlantarFasciitisMonths;
    case 'plantar_fasciitis_years_duration':
      return !hasPlantarFasciitisYears;
    case 'plantar_fasciitis_walking_aid_kind':
      return !hasPlantarFasciitisWalkingAidKind;
    case 'plantar_fasciitis_previous_location':
      return !hasPlantarFasciitisPreviousLocation;
  }

  // Ahuero Achilles Tendonitis pain
  switch (name) {
    case 'achilles_tendonitis_days_duration':
      return !hasAchillesTendonitisDays;
    case 'achilles_tendonitis_months_duration':
      return !hasAchillesTendonitisMonths;
    case 'achilles_tendonitis_years_duration':
      return !hasAchillesTendonitisYears;
    case 'achilles_tendonitis_previous_location':
      return !hasAchillesTendonitisPreviousLocation;
  }

  // Labbe Shoulder
  switch (name) {
    case 'labbe_shoulder_duration_days':
      return !hasShoulderDays;
    case 'labbe_shoulder_duration_months':
      return !hasShoulderMonths;
    case 'labbe_shoulder_duration_years':
      return !hasShoulderYears;
    case 'labbe_shoulder_exacerbating_exercises':
      return !hasShoulderExercises;
    case 'labbe_shoulder_exacerbating_throw':
      return !hasShoulderThrow;
    case 'labbe_shoulder_usualjob':
      return !hasShoulderJob;
    case 'labbe_shoulder_mri_details':
      return !hasShoulderDetails;
  }

  // Labbe Knee
  switch (name) {
    case 'labbe_knee_duration_days':
      return !hasKneeDays;
    case 'labbe_knee_duration_months':
      return !hasKneeMonths;
    case 'labbe_knee_duration_years':
      return !hasKneeYears;
    case 'labbe_knee_usualjob':
      return !hasKneeJob;
    case 'labbe_knee_mri_details':
      return !hasKneeDetails;
  }

  // Standard Foot
  switch (name) {
    case 'foot_duration_days':
      return !hasFootDays;
    case 'foot_duration_months':
      return !hasFootMonths;
    case 'foot_duration_years':
      return !hasFootYears;
    case 'foot_usualjob':
      return !hasFootJob;
    case 'foot_mri_details':
      return !hasFootDetails;
  }

  // Palmer Lower Extremity
  switch (name) {
    case 'lowext_back_pain_location':
      return !hasLowextBackPainLocation;
    case 'lowext_back_pain_side':
      return !hasLowextBackPainSide;
    case 'lowext_back_pain_severity':
      return !hasLowextBackPainSeverity;
    case 'lowext_back_pain_duration':
      return !hasLowextBackPainDuration;
    case 'lowext_back_pain_duration_days':
      return !hasLowextBackPainDays;
    case 'lowext_back_pain_duration_months':
      return !hasLowextBackPainMonths;
    case 'lowext_back_pain_duration_years':
      return !hasLowextBackPainYears;
    case 'lowext_leg_pain_location':
      return !hasLowextLegPainLocation;
    case 'lowext_leg_pain_side':
      return !hasLowextLegPainSide;
    case 'lowext_leg_pain_severity':
      return !hasLowextLegPainSeverity;
    case 'lowext_leg_pain_duration':
      return !hasLowextLegPainDuration;
    case 'lowext_leg_pain_duration_days':
      return !hasLowextLegPainDays;
    case 'lowext_leg_pain_duration_months':
      return !hasLowextLegPainMonths;
    case 'lowext_leg_pain_duration_years':
      return !hasLowextLegPainYears;
    case 'lowext_leg_weakness_side':
      return !hasLowextLegWeaknessSide;
    case 'lowext_leg_weakness_location':
      return !hasLowextLegWeaknessLocation;
    case 'lowext_sensation_side':
      return !hasLowextSensationSide;
    case 'lowext_sensation_location':
      return !hasLowextSensationLocation;
    case 'lowext_work_off':
      return !hasLowextWorkOff;
    case 'lowext_surgery_info':
      return !hasLowextSurgeryInfo;
    case 'lowext_injections_date':
      return !hasLowextInjectionsDate;
  }

  // Ahuero fu general
  switch (name) {
    case 'ahuero_fugeneral_cc_other':
      return !hasOtherComplaint;
  }

  return false;
}

export function mapApostrophes(value: string) {
  return ['’', "'"].map((char) => value.replace(/'/g, char));
}

export function getConditionalFieldValues(
  formFields: store.FormFieldWithConfig[],
): ConditionalFieldValues {
  const nameToFormFieldMap: { [name: string]: store.FormFieldWithConfig } = {};
  const nameToValueMap: { [questionName: string]: string | undefined } = {};
  for (const field of formFields) {
    nameToFormFieldMap[field.name] = field;
    nameToValueMap[field.name] = field.value;
  }

  // gleason post op rechecks + ficke standard recheck
  const painField = nameToFormFieldMap['pain_level'];
  const hasPain =
    painField && painField.value
      ? !mapApostrophes("I don't have pain anymore").includes(painField.value)
      : false;
  // fogle
  const hasPainImproved =
    painField && painField.value ? painField.value === 'Improved' : false;
  const hasPainWorsened =
    painField && painField.value ? painField.value === 'Worsened' : false;

  // fogle
  const painTime = nameToFormFieldMap['pain_time'];
  const hasPainDuringDayToDayActivities =
    painTime && painTime.value
      ? painTime.value.includes('During day-to-day activities')
      : false;

  const medication = nameToFormFieldMap['medication'];
  const hasTakenMedication =
    medication && medication.value
      ? !mapApostrophes("I haven't taken medication").includes(medication.value)
      : false;

  // ficke standard rechecks
  const sideEffects = nameToFormFieldMap['side_effects'];
  const hasSideEffect =
    sideEffects && sideEffects.value
      ? !sideEffects.value.includes('None of the above')
      : false;
  const hasNumbnessOrTingling =
    sideEffects && sideEffects.value
      ? sideEffects.value.includes('Numbness') ||
        sideEffects.value.includes('Tingling')
      : false;

  // w/ brace
  const braceUsageFrequency =
    nameToFormFieldMap['brace_usage_frequency_options'];
  const hasUsedBrace =
    braceUsageFrequency && braceUsageFrequency.value
      ? ['Daily', 'Intermittently'].includes(braceUsageFrequency.value)
      : false;

  // naik diabetes
  const checkBloodSugarBoolean =
    nameToFormFieldMap['checking_blood_sugar_boolean'];
  const hasCheckedBloodSugarReadings = checkBloodSugarBoolean
    ? checkBloodSugarBoolean.value === 'Yes'
    : false;

  // naik hypertension
  const bloodPressureMonitoringFrequency =
    nameToFormFieldMap['blood_pressure_monitoring_frequency_options'];
  const hasMonitoredBloodPressure =
    bloodPressureMonitoringFrequency && bloodPressureMonitoringFrequency.value
      ? bloodPressureMonitoringFrequency.value !== 'Never'
      : false;

  // dignity annual wellness
  const fluShotBoolean = nameToFormFieldMap['yearly_flu_shot_boolean'];
  const hasFluShot = fluShotBoolean ? fluShotBoolean.value === 'Yes' : false;
  const pneumoniaShotBoolean = nameToFormFieldMap['pneumonia_shot_boolean'];
  const hasPneumoniaShot = pneumoniaShotBoolean
    ? pneumoniaShotBoolean.value === 'Yes'
    : false;
  const exerciseFrequency = nameToFormFieldMap['strenuous_exercise_frequency'];
  const hasExercised =
    exerciseFrequency && exerciseFrequency.value
      ? exerciseFrequency.value !== '0 days'
      : false;
  const alcoholBoolean = nameToFormFieldMap['alcohol_boolean'];
  const hasDrankAlcohol = alcoholBoolean
    ? alcoholBoolean.value === 'Yes'
    : false;

  // desai heel pain
  const heelPainDuration = nameToFormFieldMap['heel_pain_duration']
    ? nameToFormFieldMap['heel_pain_duration'].value
    : null;
  const hasHeelPainDurationDays = heelPainDuration === 'Days';
  const hasHeelPainDurationMonths = heelPainDuration === 'Months';
  const hasHeelPainDurationYears = heelPainDuration === 'Years';

  const heelPainPreviousProvider = nameToFormFieldMap[
    'heel_pain_previous_provider'
  ]
    ? nameToFormFieldMap['heel_pain_previous_provider'].value
    : null;
  const hasHeelPainPreviousProvider = heelPainPreviousProvider === 'Yes';

  // desai nail pain
  const nailPainDuration = nameToFormFieldMap['nail_pain_duration']
    ? nameToFormFieldMap['nail_pain_duration'].value
    : null;
  const hasNailPainDurationDays = nailPainDuration === 'Days';
  const hasNailPainDurationMonths = nailPainDuration === 'Months';
  const hasNailPainDurationYears = nailPainDuration === 'Years';

  const nailPainPreviousProvider = nameToFormFieldMap[
    'nail_pain_previous_provider'
  ]
    ? nameToFormFieldMap['nail_pain_previous_provider'].value
    : null;
  const hasNailPainPreviousProvider = nailPainPreviousProvider === 'Yes';

  const nailPainDiabetic = nameToFormFieldMap['nail_pain_diabetic']
    ? nameToFormFieldMap['nail_pain_diabetic'].value
    : null;
  const hasNailPainDiabetic = nailPainDiabetic === 'Yes';

  const nailPainNumbness = nameToFormFieldMap['nail_pain_numbness']
    ? nameToFormFieldMap['nail_pain_numbness'].value
    : null;
  const hasNailPainNumbness = nailPainNumbness === 'Yes';

  const nailPainPreviousXray = nameToFormFieldMap['nail_pain_previous_xray']
    ? nameToFormFieldMap['nail_pain_previous_xray'].value
    : null;
  const hasNailPainPreviousXray = nailPainPreviousXray === 'Yes';

  // desai post op general
  const hasFootPain = nameToFormFieldMap['has_pain']
    ? nameToFormFieldMap['has_pain'].value === 'Yes'
    : false;
  const hasNerveBlock = nameToFormFieldMap['has_nerve_block']
    ? nameToFormFieldMap['has_nerve_block'].value === 'Yes'
    : false;
  const hasAdditionalSymptoms = nameToFormFieldMap['has_other_symptoms']
    ? nameToFormFieldMap['has_other_symptoms'].value === 'Yes'
    : false;
  const hasAdditionalComments = nameToFormFieldMap['has_something_to_share']
    ? nameToFormFieldMap['has_something_to_share'].value === 'Yes'
    : false;

  // Generic foot
  const genericFootDuration = nameToValueMap['hm_new_ortho_patient_duration'];
  const hasGenericFootDays = genericFootDuration === 'Days';
  const hasGenericFootMonths = genericFootDuration === 'Months';
  const hasGenericFootYears = genericFootDuration === 'Years';

  const hasGenericFootOtherReason =
    nameToValueMap['hm_new_ortho_patient_cc'] === 'Other';
  const hasGenericFootContext =
    nameToValueMap['hm_new_ortho_patient_onset'] ===
    'Related to an injury or event';
  const hasGenericFootPainScale =
    nameToValueMap['hm_new_ortho_patient_cc'] === 'Pain';

  // Park hip pain
  const hipPainDuration = nameToValueMap['hip_pain_duration'];
  const hasHipPainDays = hipPainDuration === 'Days';
  const hasHipPainMonths = hipPainDuration === 'Months';
  const hasHipPainYears = hipPainDuration === 'Years';

  const hasHipPainLimp = nameToValueMap['hip_pain_limp'] === 'Yes';
  const hasHipPainNight = nameToValueMap['hip_pain_night_pain'] === 'Yes';
  const hasHipPainActivities =
    nameToValueMap['hip_pain_daily_activities'] === 'Yes';
  const hasHipPainStairs = nameToValueMap['hip_pain_stairs'] === 'No';
  const hasHipPainWork = nameToValueMap['hip_pain_work'] === 'Yes';
  const hasHipPainPreviousInjury =
    nameToValueMap['hip_pain_previous_injury'] === 'Yes';

  // Park knee pain
  const kneePainSide = nameToValueMap['knee_pain_side'];
  const hasKneePainRight = kneePainSide === 'Right' || kneePainSide === 'Both';
  const hasKneePainLeft = kneePainSide === 'Left' || kneePainSide === 'Both';

  const kneePainDuration = nameToValueMap['knee_pain_duration'];
  const hasKneePainDays = kneePainDuration === 'Days';
  const hasKneePainMonths = kneePainDuration === 'Months';
  const hasKneePainYears = kneePainDuration === 'Years';

  const hasKneePainLimp = nameToValueMap['knee_pain_limp'] === 'Yes';
  const hasKneePainNight = nameToValueMap['knee_pain_night_pain'] === 'Yes';
  const hasKneePainActivities =
    nameToValueMap['knee_pain_daily_activities'] === 'Yes';
  const hasKneePainStairs = nameToValueMap['knee_pain_stairs'] === 'No';
  const hasKneePainWork = nameToValueMap['knee_pain_work'] === 'Yes';
  const hasKneePainPreviousInjury =
    nameToValueMap['knee_pain_previous_injury'] === 'Yes';

  // Ahuero NP Ankle Pain
  const anklePainDuration = nameToValueMap['ankle_pain_duration'];
  const hasAnklePainDays = anklePainDuration === 'Days';
  const hasAnklePainMonths = anklePainDuration === 'Months';
  const hasAnklePainYears = anklePainDuration === 'Years';

  const hasAnklePainPreviousLocation =
    nameToValueMap['ankle_pain_previous_provider'] === 'Yes';

  // Ahuero NP foot Pain
  const hasFootPainToesHurt = nameToValueMap['foot_pain_toes'] === 'Toes';

  const footPainDuration = nameToValueMap['foot_pain_duration'];
  const hasFootPainDays = footPainDuration === 'Days';
  const hasFootPainMonths = footPainDuration === 'Months';
  const hasFootPainYears = footPainDuration === 'Years';

  const hasFootPainPreviousLocation =
    nameToValueMap['foot_pain_previous_provider'] === 'Yes';

  // HM Diabetic foot care
  const hasFootCareDiabetic =
    nameToValueMap['hm_diabetic_foot_care_diabetes_diagnosed'] === 'Yes';
  const hasFootCareDiabeticAdult =
    nameToValueMap['hm_diabetic_foot_care_diabetes_type'] === 'Type I (Adult)';
  const hasFootCareCheckAtHome =
    nameToValueMap['hm_diabetic_foot_care_diabetes_check_sugar'] === 'Yes';
  const hasFootCareSkinChanges =
    nameToValueMap['hm_diabetic_foot_care_skin_changes'] === 'Yes';
  const hasFootCareNumbness =
    nameToValueMap['hm_diabetic_foot_care_numbness'] === 'Yes';
  const hasFootCareUlceration =
    nameToValueMap['hm_diabetic_foot_care_ulceration'] === 'Yes';
  const hasFootCareSurgery =
    nameToValueMap['hm_diabetic_foot_care_surgery'] === 'Yes';
  const hasFootCareAmputation =
    nameToValueMap['hm_diabetic_foot_care_amputation'] === 'Yes';
  const hasFootCareFootwear =
    nameToValueMap['hm_diabetic_foot_care_footwear'] === 'Yes';
  const hasFootCareFootwearOtherFacility =
    nameToValueMap['hm_diabetic_foot_care_footwear_where'] === 'Other facility';
  const hasFootCareUseTobacco =
    nameToValueMap['hm_diabetic_foot_care_tobacco'] === 'Yes';

  // Ahuero NP Plantar Fasciitis Pain
  const plantarFasciitisDuration = nameToValueMap['plantar_fasciitis_duration'];
  const hasPlantarFasciitisDays = plantarFasciitisDuration === 'Days';
  const hasPlantarFasciitisMonths = plantarFasciitisDuration === 'Months';
  const hasPlantarFasciitisYears = plantarFasciitisDuration === 'Years';

  const hasPlantarFasciitisWalkingAidKind =
    nameToValueMap['plantar_fasciitis_walking_aid'] === 'Yes';

  const hasPlantarFasciitisPreviousLocation =
    nameToValueMap['plantar_fasciitis_previous_provider'] === 'Yes';

  // Ahuero Achilles Tendonitis
  const achillesTendonitisDuration =
    nameToValueMap['achilles_tendonitis_duration'];
  const hasAchillesTendonitisDays = achillesTendonitisDuration === 'Days';
  const hasAchillesTendonitisMonths = achillesTendonitisDuration === 'Months';
  const hasAchillesTendonitisYears = achillesTendonitisDuration === 'Years';

  const hasAchillesTendonitisPreviousLocation =
    nameToValueMap['achilles_tendonitis_previous_provider'] === 'Yes';

  // Labbe Shoulder
  const shoulderDuration = nameToValueMap['labbe_shoulder_duration'];
  const hasShoulderDays = shoulderDuration === 'Days';
  const hasShoulderMonths = shoulderDuration === 'Months';
  const hasShoulderYears = shoulderDuration === 'Years';

  const shoulderExacerbating =
    nameToFormFieldMap['labbe_shoulder_exacerbating'];
  const hasShoulderExercises =
    shoulderExacerbating && shoulderExacerbating.value
      ? shoulderExacerbating.value.includes('Lifting weights')
      : false;
  const hasShoulderThrow =
    shoulderExacerbating && shoulderExacerbating.value
      ? shoulderExacerbating.value.includes(
          'Throwing or hitting a tennis serve',
        )
      : false;

  const shoulderDemands = nameToFormFieldMap['labbe_shoulder_demands'];
  const hasShoulderJob =
    shoulderDemands && shoulderDemands.value
      ? shoulderDemands.value.includes(
          'Manual Labor: including lifting; carrying; pulling; pushing; working with arms over head (e.g. mechanic; construction; laborer; etc)',
        ) ||
        shoulderDemands.value.includes(
          'Moderate: regular use of arms but not heavy',
        ) ||
        shoulderDemands.value.includes('Office environment')
      : false;
  const hasShoulderDetails = nameToValueMap['labbe_shoulder_mri'] === 'Yes';

  // Labbe Knee
  const kneeDuration = nameToValueMap['labbe_knee_duration'];
  const hasKneeDays = kneeDuration === 'Days';
  const hasKneeMonths = kneeDuration === 'Months';
  const hasKneeYears = kneeDuration === 'Years';
  const kneeDemands = nameToFormFieldMap['labbe_knee_demands'];
  const hasKneeJob =
    kneeDemands && kneeDemands.value
      ? kneeDemands.value.includes(
          'Manual labor including kneeling; squatting; climbing; carrying; etc (e.g. construction or laborer)',
        ) ||
        kneeDemands.value.includes(
          'Moderate: Walking; stairs; on feet all or most of day',
        ) ||
        kneeDemands.value.includes(
          'Office environment with frequent walking and time on your feet',
        ) ||
        kneeDemands.value.includes('Desk Job')
      : false;
  const hasKneeDetails = nameToValueMap['labbe_knee_mri'] === 'Yes';

  // Standard Foot
  const footDuration = nameToValueMap['foot_duration'];
  const hasFootDays = footDuration === 'Days';
  const hasFootMonths = footDuration === 'Months';
  const hasFootYears = footDuration === 'Years';
  const footDemands = nameToFormFieldMap['foot_demands'];
  const hasFootJob =
    footDemands && footDemands.value
      ? footDemands.value.includes(
          'Manual labor including lifting; squatting; climbing; carrying; etc (e.g. construction or laborer)',
        ) ||
        footDemands.value.includes(
          'Moderate: Walking; stairs; on your feet all or most of the day',
        ) ||
        footDemands.value.includes(
          'Office environment with frequent walking and time on your feet',
        ) ||
        footDemands.value.includes('Desk Job')
      : false;
  const hasFootDetails = nameToValueMap['foot_mri'] === 'Yes';

  // Palmer Lower Extremity
  const lowextBackPain = nameToValueMap['lowext_back_pain'];
  const hasLowextBackPainLocation = lowextBackPain === 'Yes';
  const hasLowextBackPainSide = lowextBackPain === 'Yes';
  const hasLowextBackPainSeverity = lowextBackPain === 'Yes';
  const hasLowextBackPainDuration = lowextBackPain === 'Yes';

  const lowextBackPainDuration = nameToValueMap['lowext_back_pain_duration'];
  const hasLowextBackPainDays = lowextBackPainDuration === 'Days';
  const hasLowextBackPainMonths = lowextBackPainDuration === 'Months';
  const hasLowextBackPainYears = lowextBackPainDuration === 'Years';

  const lowextLegPain = nameToValueMap['lowext_leg_pain'];
  const hasLowextLegPainLocation = lowextLegPain === 'Yes';
  const hasLowextLegPainSide = lowextLegPain === 'Yes';
  const hasLowextLegPainSeverity = lowextLegPain === 'Yes';
  const hasLowextLegPainDuration = lowextLegPain === 'Yes';

  const lowextLegPainDuration = nameToValueMap['lowext_leg_pain_duration'];
  const hasLowextLegPainDays = lowextLegPainDuration === 'Days';
  const hasLowextLegPainMonths = lowextLegPainDuration === 'Months';
  const hasLowextLegPainYears = lowextLegPainDuration === 'Years';

  const hasLowextLegWeaknessSide =
    nameToValueMap['lowext_leg_weakness'] === 'Yes';
  const hasLowextLegWeaknessLocation =
    nameToValueMap['lowext_leg_weakness'] === 'Yes';

  const hasLowextSensationSide = nameToValueMap['lowext_sensation'] === 'Yes';
  const hasLowextSensationLocation =
    nameToValueMap['lowext_sensation'] === 'Yes';

  const hasLowextWorkOff = nameToValueMap['lowext_work'] === 'No';
  const hasLowextSurgeryInfo = nameToValueMap['lowext_prior_surgery'] === 'Yes';
  const hasLowextInjectionsDate = nameToValueMap['lowext_injections'] === 'Yes';

  // Ahuero fu general
  const hasOtherComplaint = nameToValueMap['ahuero_fugeneral_cc'] === 'other';

  return {
    hasPain,
    hasPainImproved,
    hasPainWorsened,
    hasPainDuringDayToDayActivities,
    hasTakenMedication,
    hasSideEffect,
    hasNumbnessOrTingling,
    hasUsedBrace,
    hasCheckedBloodSugarReadings,
    hasMonitoredBloodPressure,
    hasFluShot,
    hasPneumoniaShot,
    hasExercised,
    hasDrankAlcohol,
    hasHeelPainDurationDays,
    hasHeelPainDurationMonths,
    hasHeelPainDurationYears,
    hasHeelPainPreviousProvider,
    hasNailPainDurationDays,
    hasNailPainDurationMonths,
    hasNailPainDurationYears,
    hasNailPainPreviousProvider,
    hasNailPainDiabetic,
    hasNailPainNumbness,
    hasNailPainPreviousXray,

    // desai post of general
    hasFootPain,
    hasNerveBlock,
    hasAdditionalSymptoms,
    hasAdditionalComments,

    // Generic Foot
    hasGenericFootOtherReason,
    hasGenericFootDays,
    hasGenericFootMonths,
    hasGenericFootYears,
    hasGenericFootContext,
    hasGenericFootPainScale,

    // Park hip pain
    hasHipPainDays,
    hasHipPainMonths,
    hasHipPainYears,
    hasHipPainLimp,
    hasHipPainNight,
    hasHipPainActivities,
    hasHipPainStairs,
    hasHipPainWork,
    hasHipPainPreviousInjury,

    // Park knee pain
    hasKneePainLeft,
    hasKneePainRight,
    hasKneePainDays,
    hasKneePainMonths,
    hasKneePainYears,
    hasKneePainLimp,
    hasKneePainNight,
    hasKneePainActivities,
    hasKneePainStairs,
    hasKneePainWork,
    hasKneePainPreviousInjury,

    // Ahuero NP Ankle Pain
    hasAnklePainDays,
    hasAnklePainMonths,
    hasAnklePainYears,
    hasAnklePainPreviousLocation,

    // Ahuero NP Foot Pain
    hasFootPainToesHurt,
    hasFootPainDays,
    hasFootPainMonths,
    hasFootPainYears,
    hasFootPainPreviousLocation,

    // HM Diabetic foot care
    hasFootCareDiabetic,
    hasFootCareDiabeticAdult,
    hasFootCareCheckAtHome,
    hasFootCareSkinChanges,
    hasFootCareNumbness,
    hasFootCareUlceration,
    hasFootCareSurgery,
    hasFootCareAmputation,
    hasFootCareFootwear,
    hasFootCareFootwearOtherFacility,
    hasFootCareUseTobacco,

    // Ahuero Achilles Tendonitis
    hasAchillesTendonitisDays,
    hasAchillesTendonitisMonths,
    hasAchillesTendonitisYears,
    hasAchillesTendonitisPreviousLocation,

    // Ahuero Plantar Fasciitis
    hasPlantarFasciitisDays,
    hasPlantarFasciitisMonths,
    hasPlantarFasciitisYears,
    hasPlantarFasciitisWalkingAidKind,
    hasPlantarFasciitisPreviousLocation,

    // Labbe Shoulder
    hasShoulderDays,
    hasShoulderMonths,
    hasShoulderYears,
    hasShoulderExercises,
    hasShoulderThrow,
    hasShoulderJob,
    hasShoulderDetails,

    // Labbe Knee
    hasKneeDays,
    hasKneeMonths,
    hasKneeYears,
    hasKneeJob,
    hasKneeDetails,

    // Standard Foot
    hasFootDays,
    hasFootMonths,
    hasFootYears,
    hasFootJob,
    hasFootDetails,

    // Palmer Lower Extremity
    hasLowextBackPainLocation,
    hasLowextBackPainSide,
    hasLowextBackPainSeverity,
    hasLowextBackPainDuration,
    hasLowextBackPainDays,
    hasLowextBackPainMonths,
    hasLowextBackPainYears,
    hasLowextLegPainLocation,
    hasLowextLegPainSide,
    hasLowextLegPainSeverity,
    hasLowextLegPainDuration,
    hasLowextLegPainDays,
    hasLowextLegPainMonths,
    hasLowextLegPainYears,
    hasLowextLegWeaknessSide,
    hasLowextLegWeaknessLocation,
    hasLowextSensationSide,
    hasLowextSensationLocation,
    hasLowextWorkOff,
    hasLowextSurgeryInfo,
    hasLowextInjectionsDate,

    // Ahuero fu general
    hasOtherComplaint,
  };
}

export function validatePageValues(
  pageFormFields: store.FormFieldWithConfig[],
  allFormFields: store.FormFieldWithConfig[],
  patient: api.Patient.Patient | null,
  practicePreference: api.Previsit.PracticePreference,
): void {
  const t = i18n.t.bind(i18n);
  for (const formField of pageFormFields.filter(
    (f) => !checkIsHidden(f, allFormFields, patient),
  )) {
    validateFormFieldValue(
      formField,
      practicePreference.collectSubscriberInfo,
      practicePreference.patientGuarantorDetailsEnabled,
      t,
    );
  }
}

export function getFormFieldAndParsedValue<T>(
  fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
  primitive: api.PrevisitFormTemplateFieldConfig.Primitive,
  currentPage?: number,
  patient?: api.Patient.Patient,
): {
  formField: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  value: T | null;
} | null {
  const formField = fields
    .filter(
      (field) =>
        (currentPage === undefined || field.page < currentPage) &&
        !checkIsHidden(field, fields, patient ?? null),
    )
    .find((field) => field.primitive === primitive && field.value);
  if (!formField) return null;
  const parsedValue = parseValue<T>(formField.value);
  return { formField, value: parsedValue };
}

const diffThreshold = 1 * 1000; // 1 second

export function isFormTouched(fields: store.FormFieldWithConfig[]): boolean {
  return fields.some(({ created, updated }) => {
    // NOTE: JSON.parse doesn't give us Date objects, so created/updated are actually date strings
    const cDate = new Date(created);
    const uDate = new Date(updated);
    return uDate.getTime() - cDate.getTime() > diffThreshold;
  });
}

export function isFormSetComplete(
  forms: store.Form[],
  patient: api.Patient.Patient | null,
  practicePreference: api.Previsit.PracticePreference,
): boolean {
  for (const form of forms) {
    const pages = _.groupBy(form.fields, (f) => f.page);
    for (const page of Object.keys(pages)) {
      try {
        validatePageValues(
          pages[page],
          form.fields,
          patient,
          practicePreference,
        );
      } catch ({ message }) {
        console.log(
          `form ${form.displayName}, page ${page} contains the following errors:\n\n${message}`,
        );
        return false;
      }
    }
  }
  return true;
}

export const DOCUMENT_FORM_NAME = 'generated_forms_and_documents';

export function isDocumentForm(formId?: string): boolean {
  return formId === DOCUMENT_FORM_NAME;
}

async function createErrorLog(
  logPrefix: string,
  previsitFormSetId: string,
  errorMsg: string,
) {
  try {
    await api.client.Log.create({
      source: 'previsit',
      level: 'error',
      data: {
        previsitFormSetId,
        msg: logPrefix + errorMsg,
      },
    });
  } catch (e) {
    logger.error(e, `${logPrefix} could not create log`);
  }
}

export async function createLog(key: string, id: string, message: string) {
  try {
    await api.client.Log.create({
      source: 'previsit',
      level: 'info',
      data: {
        id,
        key,
        message,
      },
    });
  } catch (e) {
    logger.error(e, `${key}: could not create log`);
  }
}

export async function isFormComplete(
  form: store.Form,
  formSetId: string | null,
  patient: api.Patient.Patient | null,
  practicePreference: api.Previsit.PracticePreference,
): Promise<boolean> {
  const logPrefix = `[FormStart.utils.isFormComplete]: `;
  const t = i18n.t.bind(i18n);
  const { fields } = form;
  const nonHiddenRequiredOrNonBillingDetailsFields = fields.filter(
    (field) =>
      field.primitive !== Primitive.DocumentSigner &&
      (field.primitive === Primitive.PatientBillingDetails || field.required) &&
      !checkIsHidden(field, fields, patient),
  );

  for (const field of nonHiddenRequiredOrNonBillingDetailsFields) {
    const errorMsg = `Field is required but value is invalid -> previsitFormSetId=${formSetId} previsitFormTemplateName=${
      form.name
    } previsitFormField=${JSON.stringify(field)}`;
    if (!field.value) {
      formSetId && (await createErrorLog(logPrefix, formSetId, errorMsg));
      return false;
    }
    try {
      validateFormFieldValue(
        field,
        practicePreference.collectSubscriberInfo,
        practicePreference.patientGuarantorDetailsEnabled,
        t,
      );
    } catch {
      if (form.status === api.PrevisitForm.Status.Started) {
        formSetId && (await createErrorLog(logPrefix, formSetId, errorMsg));
      }
      return false;
    }
  }

  return true;
}

export async function maybeUpdateFormStatus(
  formId: string,
  status: api.PrevisitForm.Status,
  logPrefix = '[maybeUpdateFormStatus]',
) {
  try {
    if (status === api.PrevisitForm.Status.Started) {
      await api.client.Previsit.startForm(formId);
    } else if (status === api.PrevisitForm.Status.Completed) {
      await api.client.Previsit.completeForm(formId);
    }
  } catch (e) {
    logger.error(e, `${logPrefix} could not update form status`);
  }
}

export async function updateFormScore(
  formId: string,
  formFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
  logPrefix = '[updateFormScore]',
) {
  try {
    const scores = fieldsScores(formFields);
    await api.client.Previsit.updateFormScore({
      formId,
      score: scores[api.PrevisitForm.DEFAULT_SCORE_TYPE],
      scores,
    });
  } catch (e) {
    logger.error(e, `${logPrefix} could not update form score`);
  }
}

export async function maybeUpdateGeneratedFormStatus(
  formId: string,
  forms: store.Forms,
  status: api.PrevisitForm.Status,
) {
  const logPrefix = '[maybeUpdateGeneratedFormStatus]';
  const form = forms[DOCUMENT_FORM_NAME];
  if (!form || form.status === status) return;
  if (form.status !== api.PrevisitForm.Status.Started) {
    try {
      await api.client.Previsit.startForm(formId);
    } catch (e) {
      logger.error(e, `${logPrefix} could not update form status`);
    }
  }
  await maybeUpdateFormStatus(formId, status, logPrefix);
}

export const hasIncompleteForm = (
  forms: store.Forms,
  documents: api.PrevisitDocument.DocumentWithTemplate[],
  type = FormTypes.Main,
): boolean => {
  const _forms = getFormsByType(forms, type);
  const isDocumentFormVisible =
    _forms[DOCUMENT_FORM_NAME] &&
    documents.some((document) =>
      shouldDocumentBeShown(
        document.conditions,
        _.flatten(Object.values(_forms).map((form) => form.fields)),
      ),
    );
  return Object.keys(_forms || []).some((formKey) => {
    const status = _forms[formKey].status;
    return (
      (formKey !== DOCUMENT_FORM_NAME &&
        status !== api.PrevisitForm.Status.Completed) ||
      (formKey === DOCUMENT_FORM_NAME &&
        isDocumentFormVisible &&
        !_forms[DOCUMENT_FORM_NAME].fields.find((field) => !!field.value))
    );
  });
};

export const getFormsByType = (forms: Forms, type: FormTypes) => {
  if (type === FormTypes.All) return forms;
  return _.pickBy<Form>(forms, (form) => {
    const isRecommendation = isRecommendationForm(form);
    return (
      (type === FormTypes.Recommendation && isRecommendation) ||
      (type === FormTypes.Main && !isRecommendation)
    );
  });
};

export const getBaseFormPath = (multi = false) =>
  `/form${multi ? '/multi' : ''}`;

export const getInboundPath = (path: string) => {
  const pathSplit = path.split('/');
  return `/${pathSplit[1]}/${pathSplit[2]}`;
};

export const getFormPath = (args: {
  authenticationCode: string;
  formSetId: string;
  formId: string;
  multi?: boolean;
}) => {
  const { authenticationCode, formSetId, formId, multi } = args;
  return `${getBaseFormPath(
    multi,
  )}/${authenticationCode}/${formSetId}/${formId}`;
};

export const getFormStartPath = (args: {
  authenticationCode: string;
  multi?: boolean;
  allowReview?: boolean;
}) => {
  const { multi, authenticationCode, allowReview } = args;
  const url = `${getBaseFormPath(multi)}/${authenticationCode}/start`;
  const path: LocationDescriptor = {
    pathname: url,
  };
  if (allowReview) {
    path['search'] = '?allowReview=true';
  }
  return path;
};

export const getReviewPath = ({
  authenticationCode,
  formSetId,
  formId,
  multi = false,
}: {
  authenticationCode: string;
  formSetId: string;
  formId: string;
  multi?: boolean;
}) =>
  `${getBaseFormPath(
    multi,
  )}/${authenticationCode}/review/${formSetId}/${formId}`;

export const getReviewSectionEditPath = ({
  authenticationCode,
  sectionType,
  formSetId,
  formId,
  multi = false,
}: {
  authenticationCode: string;
  formSetId: string;
  formId: string;
  sectionType: SectionType;
  multi?: boolean;
}) =>
  `${getBaseFormPath(
    multi,
  )}/${authenticationCode}/review/${formSetId}/${formId}/edit/${sectionType}`;

export const getRecommendationPath = (
  authenticationCode: string,
  multi = false,
) => `${getBaseFormPath(multi)}/${authenticationCode}/recommendations`;

export const getFormEndPath = (authenticationCode: string, multi = false) =>
  `${getBaseFormPath(multi)}/${authenticationCode}/end`;

export const getFormFeedbackPath = (
  authenticationCode: string,
  multi = false,
) => `${getBaseFormPath(multi)}/${authenticationCode}/feedback`;

export const startForm = async ({
  history,
  authenticationCode,
  formSetId,
  form,
  multiFormSet,
  setFormStatus,
  redirect,
}: {
  history: RouteComponentProps['history'];
  authenticationCode: string;
  formSetId: string;
  multiFormSet: boolean;
  form: Form;
  setFormStatus?: (args: {
    formSetId: string;
    formId: string;
    status: api.PrevisitForm.Status;
  }) => void;
  redirect?: boolean;
}) => {
  if (form.status === Status.Created) {
    !isDocumentForm(form.id) &&
      (await maybeUpdateFormStatus(form.id, api.PrevisitForm.Status.Started));
    setFormStatus &&
      setFormStatus({
        formSetId,
        formId: form.id,
        status: api.PrevisitForm.Status.Started,
      });
  }
  if (redirect) {
    history.push({
      pathname: getFormPath({
        authenticationCode,
        formSetId,
        formId: form.id,
        multi: multiFormSet,
      }),
      search: `?page=1`,
    });
  }
};

export const onFormComplete = async ({
  history,
  authenticationCode,
  multiFormSet,
  collectFeedback,
  formSets,
  skipRecommendation = false,
}: {
  history: RouteComponentProps['history'];
  authenticationCode: string;
  multiFormSet: boolean;
  collectFeedback: boolean;
  formSets: FormSets;
  skipRecommendation?: boolean;
}) => {
  const hasLastPage = !!FormSetUtils.getLastPageFormSet(formSets);
  const formSetsArray = Object.values(formSets);
  if (
    formSetsArray.some((formSet) =>
      hasIncompleteForm(formSet.forms, formSet.documents),
    ) &&
    !hasLastPage
  ) {
    history.push(
      getFormStartPath({
        authenticationCode,
        multi: multiFormSet,
      }),
    );
  } else {
    await submitEmptyFormSets(formSets, authenticationCode);
    if (!skipRecommendation && hasRecommendationForm(formSetsArray)) {
      history.push(getRecommendationPath(authenticationCode, multiFormSet));
      return;
    } else if (collectFeedback) {
      history.push({
        pathname: getFormFeedbackPath(authenticationCode, multiFormSet),
        search: '',
      });
      return;
    } else {
      history.push({
        pathname: getFormEndPath(authenticationCode, multiFormSet),
        search: '',
      });
    }
  }
};

export const submitEmptyFormSets = async (
  formSets: FormSets,
  authenticationCode: string,
): Promise<void> => {
  const logPrefix = '[submitEmptyFormSets]';
  const emptyFormSets = Object.entries(formSets).reduce<string[]>(
    (previsitFormSets, [formSetId, { completed, forms }]) => {
      if (_.isEmpty(forms) && !completed) {
        previsitFormSets.push(formSetId);
        return previsitFormSets;
      }
      return previsitFormSets;
    },
    [],
  );
  if (!emptyFormSets.length) {
    return;
  }
  try {
    await Promise.all(
      emptyFormSets.map((formSetId) =>
        completeEmptyFormSet(formSets[formSetId], authenticationCode),
      ),
    );
  } catch (err) {
    logger.error(err, `${logPrefix} failed to submit empty formsets`);
  }
};

const completeEmptyFormSet = async (
  formSet: FormSet,
  authenticationCode: string,
) => {
  const logPrefix = '[completeEmptyFormSet]';
  const FormSetStatus = api.PrevisitFormSet.Status;
  const { id: formSetId, status } = formSet;
  try {
    if (
      status &&
      ![FormSetStatus.FormSetCompleted, FormSetStatus.FormSetExpired].includes(
        status,
      )
    ) {
      await api.client.Previsit.startFormSet(formSetId, authenticationCode);
    }
    await api.client.Previsit.submitFormSet(formSetId, authenticationCode);
  } catch (err) {
    logger.error(err, `${logPrefix} failed to complete empty formset`);
  }
};

const checkHardCondition = (
  field: store.FormFieldWithConfig,
  fields: store.FormFieldWithConfig[],
  patient: api.Patient.Patient | null,
): boolean => {
  if (field.primitive === Primitive.PatientPreferredPhone) {
    const options = getPreferredPhoneOptions(fields, patient);
    return options.length > 1;
  }
  return false;
};
